import { useState } from 'react'
import { useMembers } from '../../hooks/useMembers'
import { useAuth } from '../../hooks/useAuth'
import { supabase } from '../../lib/supabase'
import { Portal } from '../../components/Portal'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Spinner } from '../../components/ui/Spinner'
import { memberColors } from '../../styles/tokens'
import { getInitials, cn } from '../../lib/utils'

const AVATAR_SIZES = { 9: 'w-9 h-9', 10: 'w-10 h-10', 12: 'w-12 h-12' }

const MemberAvatar = ({ name, color, size = 10 }) => (
  <div
    className={cn(AVATAR_SIZES[size] ?? 'w-10 h-10', 'rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0')}
    style={{ backgroundColor: memberColors[color ?? 'violet'] }}
  >
    {getInitials(name ?? '')}
  </div>
)

const RoleBadge = ({ role }) => {
  const styles = {
    admin:    'bg-primary-light text-primary',
    standard: 'bg-background text-text-secondary',
    limited:  'bg-amber-100 text-amber-700',
  }
  return (
    <span className={cn('text-xs font-medium px-2 py-0.5 rounded-full capitalize', styles[role] ?? styles.standard)}>
      {role}
    </span>
  )
}

// ── Invite modal ────────────────────────────────────────────────────────────
const InviteModal = ({ onClose, invite }) => {
  const [email,      setEmail]      = useState('')
  const [role,       setRole]       = useState('standard')
  const [inviteLink,  setInviteLink]  = useState('')
  const [loading,     setLoading]     = useState(false)
  const [error,       setError]       = useState('')
  const [emailError,  setEmailError]  = useState('')

  const handleInvite = async (e) => {
    e.preventDefault()
    if (!email.trim()) return
    setLoading(true)
    setError('')
    try {
      const result = await invite.mutateAsync({ email: email.trim(), role })
      const link = `${window.location.origin}/invite/${result.token}`
      setInviteLink(link)
      if (result.emailError) setEmailError(result.emailError)
    } catch {
      setError('Could not create invite. Please try again.')
    }
    setLoading(false)
  }

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(inviteLink) } catch { /* ignore */ }
  }

  return (
    <Portal>
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center pb-[env(safe-area-inset-bottom,0px)] sm:pb-0" onClick={onClose}>
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative bg-surface rounded-t-2xl sm:rounded-2xl w-full max-w-md p-6 shadow-card" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-base font-bold text-text-primary">Invite member</h2>
            <button onClick={onClose} className="text-text-muted p-1">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
            </button>
          </div>

          {inviteLink ? (
            <div className="space-y-4">
              <div className="flex flex-col items-center gap-2 py-2">
                {emailError
                  ? <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-amber-500"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  : <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-emerald-500"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                }
                <p className="text-sm font-semibold text-text-primary">{emailError ? 'Invite created' : 'Invite sent!'}</p>
                {emailError
                  ? <p className="text-xs text-rose-600 text-center">Email failed: {emailError}</p>
                  : <p className="text-xs text-text-muted text-center">An invitation email has been sent to <strong>{email}</strong>.</p>
                }
              </div>
              <p className="text-xs text-text-muted">Or share this link directly:</p>
              <div className="flex gap-2">
                <input readOnly value={inviteLink}
                  className="flex-1 rounded-base border border-border bg-background px-3 py-2 text-xs text-text-muted focus:outline-none" />
                <Button variant="secondary" size="sm" onClick={copyLink}>Copy</Button>
              </div>
              <Button className="w-full" size="sm" onClick={onClose}>Done</Button>
            </div>
          ) : (
            <form onSubmit={handleInvite} className="space-y-4">
              <Input label="Email address" type="email" placeholder="person@example.com"
                value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1.5">Role</label>
                <select value={role} onChange={(e) => setRole(e.target.value)}
                  className="w-full rounded-base border border-border bg-surface px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary">
                  <option value="admin">Admin — full access</option>
                  <option value="standard">Standard — most features</option>
                  <option value="limited">Limited — only assigned items</option>
                </select>
              </div>
              {error && <p className="text-sm text-rose-600">{error}</p>}
              <div className="flex gap-2">
                <Button type="button" variant="secondary" size="sm" className="flex-1" onClick={onClose}>Cancel</Button>
                <Button type="submit" size="sm" className="flex-1" disabled={loading}>
                  {loading ? <Spinner size="sm" /> : 'Send invite'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </Portal>
  )
}

// ── Manage member modal ─────────────────────────────────────────────────────
const ManageMemberModal = ({ member, onClose, changeRole, remove }) => {
  const [removing, setRemoving] = useState(false)
  const [confirm,  setConfirm]  = useState(false)

  const handleRemove = async () => {
    setRemoving(true)
    try { await remove.mutateAsync(member.user_id) } catch { /* ignore */ }
    onClose()
  }

  return (
    <Portal>
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center pb-[env(safe-area-inset-bottom,0px)] sm:pb-0" onClick={onClose}>
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative bg-surface rounded-t-2xl sm:rounded-2xl w-full max-w-md p-6 shadow-card" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-3 mb-5">
            <MemberAvatar name={member.profiles?.display_name} color={member.profiles?.member_color} />
            <div>
              <h2 className="text-base font-bold text-text-primary">{member.profiles?.display_name ?? 'Member'}</h2>
              <RoleBadge role={member.role} />
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-text-primary mb-2">Change role</p>
              <div className="flex gap-2">
                {['admin', 'standard', 'limited'].map((r) => (
                  <button key={r} onClick={() => changeRole.mutate({ userId: member.user_id, role: r })}
                    className={cn('flex-1 py-2 rounded-base text-xs font-medium capitalize border transition-colors',
                      member.role === r
                        ? 'bg-primary text-white border-primary'
                        : 'bg-surface text-text-secondary border-border hover:border-primary')}>
                    {r}
                  </button>
                ))}
              </div>
            </div>

            {confirm ? (
              <div className="space-y-2">
                <p className="text-sm text-rose-600 font-medium">Remove {member.profiles?.display_name} from the household?</p>
                <div className="flex gap-2">
                  <Button variant="secondary" size="sm" className="flex-1" onClick={() => setConfirm(false)}>Cancel</Button>
                  <Button size="sm" className="flex-1 bg-rose-600 hover:bg-rose-700 border-rose-600 text-white"
                    onClick={handleRemove} disabled={removing}>
                    {removing ? <Spinner size="sm" /> : 'Remove'}
                  </Button>
                </div>
              </div>
            ) : (
              <Button variant="secondary" size="sm" className="w-full text-rose-600 border-rose-200 hover:bg-rose-50"
                onClick={() => setConfirm(true)}>
                Remove from household
              </Button>
            )}
          </div>

          <button onClick={onClose} className="absolute top-4 right-4 text-text-muted p-1">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        </div>
      </div>
    </Portal>
  )
}

// ── Main page ───────────────────────────────────────────────────────────────
export const MembersPage = () => {
  const { user, household, isAdmin }               = useAuth()
  const { data: members = [], isLoading, invite, changeRole, remove } = useMembers()
  const [showInvite,  setShowInvite]  = useState(false)
  const [manageMember, setManageMember] = useState(null)

  // Pending invites (admin only)
  const [pendingInvites, setPendingInvites] = useState([])
  const [invitesLoaded,  setInvitesLoaded]  = useState(false)

  const loadInvites = async () => {
    if (!isAdmin || !household?.id || invitesLoaded) return
    const { data } = await supabase
      .from('invites')
      .select('id, email, role, created_at')
      .eq('household_id', household.id)
      .is('accepted_at', null)
      .order('created_at', { ascending: false })
    setPendingInvites(data ?? [])
    setInvitesLoaded(true)
  }

  // Load pending invites when component mounts for admins
  useState(() => { loadInvites() }, [isAdmin, household?.id])

  const revokeInvite = async (id) => {
    await supabase.from('invites').delete().eq('id', id)
    setPendingInvites((prev) => prev.filter((i) => i.id !== id))
  }

  const ownMembers    = members.filter((m) => !m.isShared)
  const sharedMembers = members.filter((m) =>  m.isShared)

  if (isLoading) return <div className="flex justify-center py-16"><Spinner /></div>

  return (
    <div className="px-4 py-5 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-xl font-bold text-text-primary">Members</h1>
        {isAdmin && (
          <button onClick={() => setShowInvite(true)}
            className="flex items-center gap-1.5 bg-primary text-white text-sm font-medium px-4 py-2 rounded-xl hover:bg-primary/90">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14"/></svg>
            Invite
          </button>
        )}
      </div>

      {/* Member list */}
      <div className="bg-surface rounded-xl border border-border overflow-hidden mb-5">
        {ownMembers.map((m, i) => (
          <div key={m.user_id}
            className={cn('flex items-center justify-between px-4 py-3.5', i < ownMembers.length - 1 && 'border-b border-border')}>
            <div className="flex items-center gap-3">
              <MemberAvatar name={m.profiles?.display_name} color={m.profiles?.member_color} />
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-text-primary">{m.profiles?.display_name ?? 'Unknown'}</p>
                  {m.user_id === user?.id && (
                    <span className="text-[10px] font-medium text-text-muted">(you)</span>
                  )}
                </div>
                <RoleBadge role={m.role} />
              </div>
            </div>
            {isAdmin && m.user_id !== user?.id && (
              <button onClick={() => setManageMember(m)}
                className="text-xs text-text-muted hover:text-primary font-medium transition-colors px-2 py-1">
                Manage
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Shared members from linked household */}
      {sharedMembers.length > 0 && (
        <div className="mb-5">
          <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">Shared from linked household</h2>
          <div className="bg-surface rounded-xl border border-border overflow-hidden">
            {sharedMembers.map((m, i) => (
              <div key={m.user_id}
                className={cn('flex items-center gap-3 px-4 py-3.5', i < sharedMembers.length - 1 && 'border-b border-border')}>
                <MemberAvatar name={m.profiles?.display_name} color={m.profiles?.member_color} />
                <div>
                  <p className="text-sm font-semibold text-text-primary">{m.profiles?.display_name ?? 'Unknown'}</p>
                  <span className="text-[10px] text-text-muted">Shared member</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending invites */}
      {isAdmin && pendingInvites.length > 0 && (
        <div className="mb-5">
          <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">Pending invites</h2>
          <div className="bg-surface rounded-xl border border-border overflow-hidden">
            {pendingInvites.map((inv, i) => (
              <div key={inv.id}
                className={cn('flex items-center justify-between px-4 py-3.5', i < pendingInvites.length - 1 && 'border-b border-border')}>
                <div>
                  <p className="text-sm font-medium text-text-primary">{inv.email}</p>
                  <RoleBadge role={inv.role} />
                </div>
                <button onClick={() => revokeInvite(inv.id)} className="text-xs text-text-muted hover:text-rose-500 transition-colors px-2 py-1">
                  Revoke
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {!isAdmin && ownMembers.length === 0 && (
        <p className="text-sm text-text-muted text-center py-12">No members found.</p>
      )}

      {showInvite && (
        <InviteModal invite={invite} onClose={() => { setShowInvite(false); loadInvites() }} />
      )}

      {manageMember && (
        <ManageMemberModal member={manageMember} changeRole={changeRole} remove={remove}
          onClose={() => setManageMember(null)} />
      )}
    </div>
  )
}
